import axios from 'axios';

// URLs for the backend services
const BACKEND_URLS = {
  local: 'http://localhost:8080/dsl_to_latex',
  ngrok: 'https://ed03-2607-fb92-1786-1d97-e5d0-5e12-9465-c125.ngrok-free.app/dsl_to_latex',
  railway: 'https://mathlatech-v10-production-1880.up.railway.app/dsl_to_latex',
};

// Trigger new deployment
const END_PRIORITY = window.location.hostname === 'localhost' 
  ? ['local', 'ngrok', 'railway']  // Local development: prefer local first
  : ['ngrok', 'railway'];          // Production: ngrok primary, railway fallback

// Cache for backend availability
let backendStatus = {
  local: null, 
  ngrok: null,
  railway: null
};

let currentBackend = null;
let lastCheck = 0;
const CHECK_INTERVAL = 30000; // 30 seconds

// Mock data for testing (preserved from original)
const expressionMap = {
  // Basic arithmetic
  '2 + 3': '2 + 3 = 5',
  'x^2': 'x^{2}',
  
  // Trigonometric functions
  'sin(x)': '\\sin(x)',
  'cos(x)': '\\cos(x)',
  
  // Calculus operations
  'diff(sin(x), x)': '\\frac{d}{dx}[\\sin(x)] = \\cos(x)',
  'integrate(x^2, x)': '\\int x^{2} dx = \\frac{x^{3}}{3} + C',
  
  // Advanced vector calculus - Jacobian
  'jacobian(vector(pow(x,2), mul(x, sin(y))), vector(x,y))': '\\begin{bmatrix} \\frac{\\partial}{\\partial x}[x^2] & \\frac{\\partial}{\\partial y}[x^2] \\\\ \\frac{\\partial}{\\partial x}[x\\sin(y)] & \\frac{\\partial}{\\partial y}[x\\sin(y)] \\end{bmatrix} = \\begin{bmatrix} 2x & 0 \\\\ \\sin(y) & x\\cos(y) \\end{bmatrix}',
  
  // Hessian matrix
  'hessian(mul(pow(x,2), y), vector(x,y))': '\\begin{bmatrix} \\frac{\\partial^2}{\\partial x^2}[x^2y] & \\frac{\\partial^2}{\\partial x \\partial y}[x^2y] \\\\ \\frac{\\partial^2}{\\partial y \\partial x}[x^2y] & \\frac{\\partial^2}{\\partial y^2}[x^2y] \\end{bmatrix} = \\begin{bmatrix} 2y & 2x \\\\ 2x & 0 \\end{bmatrix}',
  
  // Limits with evaluation
  'eval(limit(mul(x, sin(div(1, x))), x, 0))': '\\lim_{x \\to 0} x \\sin\\left(\\frac{1}{x}\\right) = 0',
  'eval(limit(pow(add(1, div(1, x)), x), x, "inf"))': '\\lim_{x \\to \\infty} \\left(1 + \\frac{1}{x}\\right)^x = e',
  
  // Additional DSL functions
  'pow(x, 2)': 'x^{2}',
  'mul(x, y)': 'xy',
  'add(x, y)': 'x + y',
  'div(x, y)': '\\frac{x}{y}',
  'vector(x, y)': '\\begin{bmatrix} x \\\\ y \\end{bmatrix}'
};

const sampleLatexExpressions = [
  'x^{2} + y^{2}',
  '\\frac{a}{b}',
  '\\sqrt{x + 1}',
  '\\sin(\\theta)',
  '\\int_{0}^{1} x dx',
  '\\sum_{i=1}^{n} i^{2}'
];

// Original getApiUrl function (preserved)
const getApiUrl = () => {
  // Check for environment variable first
  return process.env.REACT_APP_API_URL;
};

// Test individual backend for smart switching
const testBackend = async (backendKey) => {
  try {
    const url = BACKEND_URLS[backendKey];
    const headers = { 'Content-Type': 'application/json' };

    if (url.includes('ngrok')) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ input: 'eval(add(1, 1))' }),
      signal: AbortSignal.timeout(3000) // 3-second timeout like in the notebook
    });

    return response.ok;
  } catch (error) {
    // Silent failure in production
    if (process.env.NODE_ENV === 'development') {
      console.log(`Backend ${backendKey} unavailable:`, error.message);
    }
    return false;
  }
};

// Smart backend selection
const selectBestBackend = async (forceCheck = false) => {
  const now = Date.now();
  
  if (!forceCheck && currentBackend && (now - lastCheck) < CHECK_INTERVAL) {
    return currentBackend;
  }
  
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Checking backend availability...');
  }
  
  for (const backend of END_PRIORITY) {
    const isAvailable = await testBackend(backend);
    backendStatus[backend] = isAvailable;
    
    if (isAvailable) {
      currentBackend = backend;
      lastCheck = now;
      
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Using ${backend} backend: ${BACKEND_URLS[backend]}`);
      }
      
      return backend;
    }
  }
  
  // No backends available - will fall back to mock
  if (process.env.NODE_ENV === 'development') {
    console.warn('⚠️ All backends unavailable, will use mock backend');
  }
  
  currentBackend = null;
  return null;
};

// Enhanced sendToLiveBackend with smart switching and mock fallback
export const sendToLiveBackend = async (expr) => {
  try {
    // Try smart backend selection first
    const backend = await selectBestBackend();
    
    if (backend) {
      const apiUrl = BACKEND_URLS[backend];
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (apiUrl.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true';
      }
      
      const response = await axios.post(apiUrl, 
        { input: expr },
        { headers }
      );
      
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`📡 Response from ${currentBackend}:`, response.data);
      }
      
      // Check if response contains plot data
      if (response.data.type && response.data.data) {
        console.log('API: Found plot data in smart backend response:', response.data);
        return response.data;  // Return full plot data object
      } else {
        return response.data.latex;  // Return latex for non-plot responses
      }
    } else {
      // Fallback to original getApiUrl logic if smart switching fails
      const API_URL = getApiUrl();
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (API_URL.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true';
      }
      
      const response = await axios.post(API_URL, 
        { input: expr },
        { headers }
      );
      
      console.log('Backend response:', response.data);
      // Check if response contains plot data
      if (response.data.type && response.data.data) {
        console.log('API: Found plot data in response:', response.data);
        return response.data;  // Return full plot data object
      } else {
        return response.data.latex;  // Return latex for non-plot responses
      }
    }
  } catch (error) {
    console.error('Error calling live backend:', error);
    
    // Final fallback to mock backend
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Falling back to mock backend...');
    }
    
    return await sendToMockBackend(expr);
  }
};

// Mock backend (preserved from original)
export const sendToMockBackend = async (expr) => {
  await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
  
  if (expressionMap[expr]) {
    return expressionMap[expr];
  }
  
  const index = Math.floor(Math.random() * sampleLatexExpressions.length);
  return sampleLatexExpressions[index];
};

// Original test backend connection (preserved)
export const testBackendConnection = async () => {
  try {
    const API_URL = getApiUrl();
    const headers = { 'Content-Type': 'application/json' };
    if (API_URL.includes('ngrok')) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    const response = await axios.post(API_URL, { input: '' }, { headers });
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

// Development-only functions for backend status (new)
export const getBackendStatus = process.env.NODE_ENV === 'development' ? () => {
  return {
    current: currentBackend,
    status: backendStatus,
    urls: BACKEND_URLS,
    fallbackUrl: getApiUrl()
  };
} : undefined;

export const switchToBackend = process.env.NODE_ENV === 'development' ? async (backendKey) => {
  if (!BACKEND_URLS[backendKey]) {
    throw new Error(`Unknown backend: ${backendKey}`);
  }
  
  const isAvailable = await testBackend(backendKey);
  if (isAvailable) {
    currentBackend = backendKey;
    lastCheck = Date.now();
    console.log(`🔄 Manually switched to ${backendKey} backend`);
    return true;
  } else {
    throw new Error(`Backend ${backendKey} is not available`);
  }
} : undefined;
